import { createFeature, createReducer, on } from '@ngrx/store';

import { Registration } from 'app/features/manual-input-registration/models';
import {
    matchRowRegistrationKeyWithActivity,
    registrationListToRowRegistrationStatusDict,
    registrationToRowRegistrationStatusDict,
    RowRegistrationStatusDict,
} from 'app/shared/features/map-row-selector/models';
import { Credentials, done, error, getInitialProgressState, ProgressState, running } from 'app/shared/models';
import {
    addRegistrationsToCache,
    resetRegistrationState,
    rewriteServerRowRegistrationsForActivity,
    sendCachedRegistrations,
    sendCachedRegistrationsFailure,
    sendCachedRegistrationsSuccess,
} from 'app/state/registrations/registrations.actions';
import { userSessionClosed } from 'app/state/user-session/user-session.actions';

export const registrationsFeatureKey = 'registrations';

export interface RegistrationState {
    cachedRegistrations: { [key: number]: Registration };
    cachedRegistrationCounter: number;
    sendProgress: ProgressState;
    serverRowRegistrations: RowRegistrationStatusDict;
    previousUserSessionCredentials: Credentials | undefined;
}

export const initialState: RegistrationState = {
    cachedRegistrations: [],
    cachedRegistrationCounter: 0,
    sendProgress: getInitialProgressState(),
    serverRowRegistrations: {},
    previousUserSessionCredentials: undefined,
};

export const reducer = createReducer(
    initialState,
    on(addRegistrationsToCache, (state, action) => {
        const newCachedRegistrations: { [key: number]: Registration } = {};
        let cachedRegistrationCounter = state.cachedRegistrationCounter;
        for (const registration of action.registrations) {
            newCachedRegistrations[cachedRegistrationCounter++] = registration;
        }
        return {
            ...state,
            cachedRegistrations: { ...state.cachedRegistrations, ...newCachedRegistrations },
            cachedRegistrationCounter,
        };
    }),
    on(sendCachedRegistrations, (state) => ({
        ...state,
        sendProgress: running(),
    })),
    on(sendCachedRegistrationsSuccess, (state, { sentRegistrations }) => {
        const sentRegistrationKeys = Object.keys(sentRegistrations).map(Number);
        const newCachedRegistrations = { ...state.cachedRegistrations };

        for (const sentRegistrationKey of sentRegistrationKeys) {
            delete newCachedRegistrations[sentRegistrationKey];
        }

        const cachedRegistrationCounter = Object.keys(newCachedRegistrations).length
            ? state.cachedRegistrationCounter
            : 0;

        const sentRowRegistrationStatusDict = registrationListToRowRegistrationStatusDict(
            Object.values(sentRegistrations),
        );
        return {
            ...state,
            cachedRegistrations: newCachedRegistrations,
            cachedRegistrationCounter,
            sendProgress: done(),
            serverRowRegistrations: {
                ...state.serverRowRegistrations,
                ...sentRowRegistrationStatusDict,
            },
        };
    }),
    on(sendCachedRegistrationsFailure, (state, { errorMessage }) => ({
        ...state,
        sendProgress: error(errorMessage),
    })),
    on(rewriteServerRowRegistrationsForActivity, (state, { activityNumber, rowRegistrations }) => {
        const oldServerRegistrations = { ...state.serverRowRegistrations };
        const cleanedUpServerRegistrations = Object.keys(oldServerRegistrations)
            .filter(
                (rowRegistrationKey) =>
                    !matchRowRegistrationKeyWithActivity({
                        rowRegistrationKey,
                        activityNumber,
                    }),
            )
            .reduce((dict: RowRegistrationStatusDict, key) => {
                dict[key] = oldServerRegistrations[key];
                return dict;
            }, {});
        return {
            ...state,
            serverRowRegistrations: {
                ...cleanedUpServerRegistrations,
                ...registrationToRowRegistrationStatusDict({ activityNumber, rowRegistrations }),
            },
        };
    }),
    on(resetRegistrationState, () => ({
        ...initialState,
    })),
    on(userSessionClosed, (state, { userSession }) => ({
        ...state,
        previousUserSessionCredentials: userSession.credentials,
    })),
);

export const registrationsFeature = createFeature({
    name: registrationsFeatureKey,
    reducer,
});
